import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import moment from 'moment';
import { forkJoin } from 'rxjs';
import { HttpService, MessageService, UserService } from 'src/app/core';
import { ConstantConfig } from 'src/app/core/config/constant.config';
import { PathConfig } from 'src/app/core/config/path.config';
import { Utility } from 'src/app/core/utils/utility';

interface WarningRow {
  warning: any;
  transaction: any;
  balances: any[];
  reviewTransactionId: any;
}

@Component({
  selector: 'app-loan-transaction-warning-modal',
  templateUrl: './loan-transaction-warning-modal.component.html',
  styleUrls: ['./loan-transaction-warning-modal.component.scss'],
  standalone: false,
})
export class LoanTransactionWarningModalComponent implements OnInit {
  @Input() warnings: any[] = [];
  @Input() loanID: any;
  rows: WarningRow[] = [];
  isReviewing = false;
  isExporting = false;
  confirmReview = false;
  printing = false;
  DATE_FORMAT_MMDDYYYY = ConstantConfig.DATE_FORMAT_MMDDYYYY;
  FULL_DATE_FORMAT = ConstantConfig.DATE_FORMAT;
  errorText = 'There is an unexpected error with this transaction, please contact the support team.';
  private expanded = new Set<number>();

  constructor(
    public activeModal: NgbActiveModal,
    private httpService: HttpService,
    private msgService: MessageService,
    private userService: UserService
  ) {}

  ngOnInit(): void {
    this.rows = this.buildRows();
  }

  isExpanded(index: number): boolean {
    return this.expanded.has(index);
  }

  toggle(index: number): void {
    if (this.expanded.has(index)) {
      this.expanded.delete(index);
      return;
    }
    this.expanded.add(index);
  }

  get warningMessages(): any[] {
    return this.warningEntries()
      .map((entry) => entry.warning)
      .filter((warning) => warning?.message);
  }

  displayPaymentId(transaction: any): string {
    const value = transaction?.payment_id;
    return value === null || value === undefined || value === '' ? '-' : String(value);
  }

  get warningPaymentIds(): string[] {
    const ids = this.rows
      .map((row) => row?.transaction?.payment_id)
      .filter((id) => id !== null && id !== undefined && id !== '' && id !== '-')
      .map((id) => String(id));
    return [...new Set(ids)];
  }

  exportWarningTransactions(): void {
    if (this.isExporting) {
      return;
    }

    const paymentIds = this.warningPaymentIds;
    const loanId = this.loanID || this.rows[0]?.transaction?.loan_id;
    if (!paymentIds.length || !loanId) {
      return;
    }

    this.isExporting = true;
    const url =
      `${PathConfig.LOAN_TRANSACTIONS}/export` +
      `?year=ALL` +
      `&loan_id=${loanId}` +
      `&limit=${paymentIds.length}` +
      `&page=1` +
      `&type=` +
      `&sortBy=payment_effective_date` +
      `&sortOrder=desc` +
      `&exportType=basic` +
      `&payment_ids=${paymentIds.join(',')}`;

    this.httpService.get(url).subscribe({
      next: (res) => {
        this.isExporting = false;
        Utility.exportURLToExcel(res, 'loan-transactions');
      },
      error: (err) => {
        this.isExporting = false;
        const msgList = Utility.getErrorMessage(err.error);
        if (msgList.length) {
          msgList.forEach((msg) => this.msgService.showError(msg));
          return;
        }
        this.msgService.showError(err?.error?.message || 'Export failed.');
      }
    });
  }

  paymentIdHref(row: WarningRow): string | null {
    const paymentId = row?.transaction?.payment_id;
    const loanId = row?.transaction?.loan_id || this.loanID;
    const clientId = this.userService.getClientId();
    if (!paymentId || paymentId === '-' || !loanId || !clientId) {
      return null;
    }
    return `/${clientId}/servicing/queues-detail/${loanId}/transactions?payment_id=${encodeURIComponent(paymentId)}`;
  }

  displayPaymentInfo(transaction: any): string {
    const value = transaction?.payment_info_code_slug
      ?? transaction?.payment_info_code?.code
      ?? transaction?.code;
    if (value === null || value === undefined || value === '') {
      return '-';
    }
    return String(value).replace(/_/g, ' ');
  }

  displayEffectiveDate(transaction: any): string {
    const value = transaction?.payment_effective_date ?? transaction?.effective_date ?? transaction?.date;
    if (!value) {
      return '-';
    }
    const parsed = moment(value);
    return parsed.isValid() ? parsed.format('MM/DD/YYYY') : String(value);
  }

  displayStatus(transaction: any): string {
    const value = transaction?.payment_status ?? transaction?.status;
    return value === null || value === undefined || value === '' ? '-' : String(value);
  }

  balances(row: WarningRow | any): any[] {
    if (Array.isArray(row?.balances)) {
      return row.balances;
    }
    if (Array.isArray(row?.balance)) {
      return row.balance;
    }
    return [];
  }

  displayBalance(value: any): string {
    if (value === null || value === undefined || value === '') {
      return '-';
    }
    const numeric = Number(value);
    if (!isNaN(numeric)) {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(numeric);
    }
    return String(value);
  }

  isNegativeAfter(balance: any): boolean {
    const after = Number(balance?.after);
    return balance?.after !== null && balance?.after !== undefined && balance?.after !== '' && !isNaN(after) && after < 0;
  }

  isNegative(value: any): boolean {
    if (value === null || value === undefined || value === '') {
      return false;
    }
    const numeric = Number(String(value).replace(/[$,]/g, ''));
    return !isNaN(numeric) && numeric < 0;
  }

  isNegativeBalanceAmount(row: WarningRow, field: string): boolean {
    return this.balances(row).some((balance) => {
      if (!this.balanceMatchesField(balance?.key, field)) {
        return false;
      }
      const amount = balance?.amount ?? balance?.after;
      return this.isNegative(amount);
    });
  }

  private balanceMatchesField(key: any, field: string): boolean {
    const balanceKey = String(key || '').toLowerCase().replace(/-/g, '_');
    const fieldKey = String(field || '').toLowerCase().replace(/-/g, '_');
    if (!balanceKey || !fieldKey) {
      return false;
    }
    if (balanceKey === fieldKey) {
      return true;
    }
    const strippedField = fieldKey.replace(/^remaining_/, '').replace(/_balance$/, '');
    return balanceKey === strippedField
      || balanceKey === `${strippedField}_balance`
      || balanceKey === `remaining_${strippedField}`
      || balanceKey === `remaining_${strippedField}_balance`;
  }

  convertPstToLocalTime(d: any) {
    if (!d) {
      return d;
    }
    return moment(moment.tz(d, ConstantConfig.SERVER_TIMEZONE))?.local()?.format('MM/DD/YYYY hh:mm:ss A');
  }

  checkBgResponse(bg_response: any) {
    if (bg_response && bg_response !== '') {
      const parsed = typeof bg_response === 'string' ? JSON.parse(bg_response) : bg_response;
      if (parsed?.status === 1) {
        return true;
      }
      if (parsed?.status === 0) {
        return false;
      }
      return true;
    }
    return true;
  }

  jsonParse(item: any) {
    if (item && item !== '') {
      const parsed = typeof item === 'string' ? JSON.parse(item) : item;
      return parsed?.error;
    }
    return '';
  }

  addVariables(var1: any = 0, var2: any = 0, var3: any = 0): number {
    const num1 = isNaN(Number(var1)) ? 0 : Number(var1);
    const num2 = isNaN(Number(var2)) ? 0 : Number(var2);
    const num3 = isNaN(Number(var3)) ? 0 : Number(var3);
    return num1 + num2 + num3;
  }

  close(): void {
    this.activeModal.dismiss();
  }

  isUnreviewed(warning: any): boolean {
    return !warning?.reviewed_at;
  }

  markReviewed(): void {
    const pending = this.warningEntries().filter((entry) => this.isUnreviewed(entry.warning));
    if (!pending.length) {
      this.activeModal.close('reviewed');
      return;
    }

    const calls = pending.map((entry) => {
      const transactionId = entry.reviewTransactionId;
      const warningId = entry.warning?.id ?? entry.warning?.warning_id;
      const url = `${PathConfig.LOAN_TRANSACTION_WARNINGS}/${transactionId}/review`;
      return this.httpService.put(url, { warning_id: String(warningId) });
    });

    this.isReviewing = true;
    forkJoin(calls).subscribe({
      next: () => {
        this.isReviewing = false;
        this.activeModal.close('reviewed');
      },
      error: (err) => {
        this.isReviewing = false;
        const msgList = Utility.getErrorMessage(err.error);
        msgList.forEach((msg) => this.msgService.showError(msg));
      }
    });
  }

  private warningEntries(): Array<{ warning: any; reviewTransactionId: any }> {
    const entries: Array<{ warning: any; reviewTransactionId: any }> = [];
    (this.warnings || []).forEach((item) => {
      if (Array.isArray(item?.warnings)) {
        item.warnings.forEach((warning: any) => {
          entries.push({
            warning,
            reviewTransactionId: item?.transaction_id ?? warning?.transaction_id
          });
        });
        return;
      }
      entries.push({
        warning: item,
        reviewTransactionId: item?.transaction_id
      });
    });
    return entries;
  }

  private buildRows(): WarningRow[] {
    const rows: WarningRow[] = [];
    this.warningEntries().forEach((entry) => {
      const transactions = Array.isArray(entry.warning?.data?.transactions) ? entry.warning.data.transactions : [];
      transactions.forEach((transaction: any) => {
        const wrapperBalances = Array.isArray(transaction?.balances)
          ? transaction.balances
          : Array.isArray(transaction?.balance)
            ? transaction.balance
            : [];
        const nested = transaction?.transaction || transaction;
        const nestedBalances = Array.isArray(nested?.balances)
          ? nested.balances
          : Array.isArray(nested?.balance)
            ? nested.balance
            : [];
        rows.push({
          warning: entry.warning,
          transaction: nested,
          balances: wrapperBalances.length ? wrapperBalances : nestedBalances,
          reviewTransactionId: entry.reviewTransactionId
        });
      });
    });
    return rows;
  }
}
